import React, {useState, useCallback, useEffect} from 'react';
import produce from 'immer';
import {useTranslation} from 'react-i18next';
import SMCButton from '../../../components/Simple/SMCButton';
import SMCModal from '../../../components/core/SMCModal';
import SMCModalBody from '../../../components/core/SMCModal/SMCModalBody';
import SMCModalButtonContainer from '../../../components/core/SMCModal/SMCModalButtonContainer';
import {SMCTab, SMCTabsContainer} from '../../../components/core/SMCTabs';
import PreviewTab from './TemplateTabs/PreviewTab';
import EditTab from './TemplateTabs/EditTab';
import {setProperty} from '../../../utils/utils';
import AdministratorsTab from './TemplateTabs/AdministratorsTab';
import UsersTab from './TemplateTabs/UsersTab';
import {TemplateSchema} from '../../../api/DBSchema/Template';
import {useSMCSnackbar} from '../../../utils/hooks';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Api from '../../../api/SMPFetchHelper';
import {useSMCErrorHandler} from '../../../utils/errorUtils';

function TemplateEditorModal({
    isOpen,
    onRequestClose,
    onError,
    onSuccess,
    templateName,
    defaultObject,
    editShortBody,
    isGlobal=false,
    hasUsersTab=true,
    hasAdministratorsTab=true
}) {
    const { t } = useTranslation();
    const { enqueueErrorSnackbar } = useSMCSnackbar();
    const { getErrorMessage } = useSMCErrorHandler();

    const [templateObject, setTemplateObject] = useState(defaultObject);
    const [selectedTab, setSelectedTab] = useState('preview');
    
    const setTemplateObjectValue = useCallback((paramPath, newValue) => {
        setTemplateObject(templateObject => produce(templateObject, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const onFinish = useCallback(() => {
        if (!templateObject[TemplateSchema.messageData][TemplateSchema.messageDataSchema.body]) {
            enqueueErrorSnackbar(t('com.sofaware.db.SETTINGS_NOTIFICATIONS_EMPTY_BODY'), {
                anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'bottom'
                }
            });
            return;            
        }
        if (!templateObject[TemplateSchema.messageData][TemplateSchema.messageDataSchema.subject]) {
            enqueueErrorSnackbar(t('com.sofaware.db.SETTINGS_NOTIFICATIONS_EMPTY_SUBJECT'), {
                anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'bottom'
                }
            });
            return;            
        }
        onSuccess(templateObject);
    }, [onSuccess, templateObject, enqueueErrorSnackbar, t]);

    const handleSelectTab = useCallback((tabName) => {
        setSelectedTab(tabName);
    }, []);
    
    const handleDefaultButtonClick = useCallback(() => {
        if (window.confirm(t('SMC_MESSEGE_DEFAULT_QUES'))) {
            Api.getTemplateDefault(templateName, isGlobal)
            .then(resp => {
                const respData = resp.data.data;
                const messageData = respData[TemplateSchema.messageData];
                setTemplateObjectValue([TemplateSchema.messageData], messageData);
            })
            .catch(e => {
                const errorMessage = getErrorMessage(e);
                alert(errorMessage);
            });
        }
    }, [templateName, isGlobal, setTemplateObjectValue, getErrorMessage, t]);
    

    useEffect(() => {
        if (isOpen) {
            setTemplateObject(defaultObject);
        }
    }, [isOpen, defaultObject]);

    return (
        <SMCModal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            width={750}
            height={590}
            title={t('SMC_MESSEGE_TITLE')}
        >
            <SMCModalBody lineHeight="24px">
                <SMCTabsContainer tabOnClick={handleSelectTab} style={{ padding: '10px' }}>
                    <SMCTab title={t('SMC_MESSEGE_TAB0')} id="preview">
                        <PreviewTab
                            templateObject={templateObject}
                            previewShortBody={editShortBody}
                            templateName={templateName}
                            isGlobal={isGlobal}
                        />
                    </SMCTab>
                    <SMCTab title={t('SMC_MESSEGE_TAB1')} id="edit">
                        <EditTab
                            templateObject={templateObject}
                            editShortBody={editShortBody}
                            setTemplateObjectValue={setTemplateObjectValue}
                        />
                    </SMCTab>
                    {
                        hasAdministratorsTab && (
                            <SMCTab title={t('SMC_MESSEGE_TAB2')} id="admins">
                                <AdministratorsTab
                                    templateObject={templateObject}
                                    setTemplateObjectValue={setTemplateObjectValue}
                                />
                            </SMCTab>
                        )
                    }
                    {
                        !isGlobal && hasUsersTab && (
                            <SMCTab title={t('SMC_MESSEGE_TAB3')} id="users">
                                <UsersTab
                                    templateObject={templateObject}
                                    setTemplateObjectValue={setTemplateObjectValue}
                                />
                            </SMCTab>
                        )
                    }
                </SMCTabsContainer>
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={onFinish}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    {t('SMC_GEN_OK')}
                </SMCButton>
                <SMCButton onClick={onRequestClose}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
                {
                    selectedTab === 'edit' && (
                        <SMCButton onClick={handleDefaultButtonClick}>
                            {t('SMC_GEN_DEFAULT')}
                        </SMCButton>
                    )
                }
            </SMCModalButtonContainer>
        </SMCModal>
    );
}

export default React.memo(TemplateEditorModal);